import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, MapPin, Trash2, Crosshair, Pencil, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

function detectCoordIssue(latStr: string, lngStr: string): string | null {
  if (!latStr && !lngStr) return null;
  const lat = Number(latStr);
  const lng = Number(lngStr);
  const latBad = latStr !== "" && (!Number.isFinite(lat) || Math.abs(lat) > 90);
  const lngBad = lngStr !== "" && (!Number.isFinite(lng) || Math.abs(lng) > 180);
  if (!latBad && !lngBad) return null;
  // Heuristic: UTM/projected coords are typically large integers (>1000) with no decimal sign issues
  const looksUtm = (Math.abs(lat) > 1000) || (Math.abs(lng) > 1000);
  if (looksUtm) {
    return "These look like UTM or projected coordinates (meters), not GPS degrees. Use a converter or click \"Use my current location\" to capture decimal-degree GPS values.";
  }
  return "Coordinates are out of range. Latitude must be between -90 and 90; longitude between -180 and 180.";
}

interface FieldSite {
  id: string;
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  radius_m: number;
  active: boolean;
}

export function FieldSitesManager() {
  const { user } = useAuth();
  const [sites, setSites] = useState<FieldSite[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<FieldSite | null>(null);
  const [form, setForm] = useState({ name: "", address: "", latitude: "", longitude: "", radius_m: "150" });
  const [loading, setLoading] = useState(false);

  async function load() {
    const { data, error } = await supabase
      .from("field_sites")
      .select("*")
      .order("name");
    if (error) { toast.error(error.message); return; }
    setSites((data ?? []) as FieldSite[]);
  }

  useEffect(() => { load(); }, []);

  function openCreate() {
    setEditing(null);
    setForm({ name: "", address: "", latitude: "", longitude: "", radius_m: "150" });
    setOpen(true);
  }

  function openEdit(s: FieldSite) {
    setEditing(s);
    setForm({
      name: s.name,
      address: s.address ?? "",
      latitude: String(s.latitude),
      longitude: String(s.longitude),
      radius_m: String(s.radius_m),
    });
    setOpen(true);
  }

  function useCurrentLocation() {
    if (!navigator.geolocation) {
      toast.error("Geolocation not available in this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) }));
        toast.success("Location captured");
      },
      (err) => toast.error("Could not get location: " + err.message),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  async function save() {
    if (!user) return;
    if (!form.name.trim() || !form.latitude || !form.longitude) {
      toast.error("Name, latitude and longitude are required");
      return;
    }
    const lat = Number(form.latitude);
    const lng = Number(form.longitude);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      toast.error("Latitude must be between -90 and 90");
      return;
    }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      toast.error("Longitude must be between -180 and 180");
      return;
    }
    const payload = {
      name: form.name.trim(),
      address: form.address.trim() || null,
      latitude: Math.round(lat * 1e6) / 1e6,
      longitude: Math.round(lng * 1e6) / 1e6,
      radius_m: Number(form.radius_m) || 150,
    };
    setLoading(true);
    if (editing) {
      const { error } = await supabase.from("field_sites").update(payload).eq("id", editing.id);
      setLoading(false);
      if (error) { toast.error(error.message); return; }
      toast.success("Site updated");
    } else {
      const { error } = await supabase.from("field_sites").insert({ ...payload, created_by: user.id });
      setLoading(false);
      if (error) { toast.error(error.message); return; }
      toast.success("Site created");
    }
    setOpen(false);
    load();
  }

  async function toggleActive(s: FieldSite) {
    const { error } = await supabase.from("field_sites").update({ active: !s.active }).eq("id", s.id);
    if (error) { toast.error(error.message); return; }
    load();
  }

  async function remove(s: FieldSite) {
    if (!confirm(`Delete site "${s.name}"? Groups linked to it will lose the link.`)) return;
    const { error } = await supabase.from("field_sites").delete().eq("id", s.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Site deleted");
    load();
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Field Sites</CardTitle>
          <CardDescription>Hospitals, clinics and other placement locations with geofence radius.</CardDescription>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> New Site</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit site" : "New field site"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Site name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Muhimbili National Hospital" />
              </div>
              <div>
                <Label className="text-xs">Address (optional)</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Latitude</Label>
                  <Input type="number" step="any" min={-90} max={90} value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} placeholder="-6.812345" />
                </div>
                <div>
                  <Label className="text-xs">Longitude</Label>
                  <Input type="number" step="any" min={-180} max={180} value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} placeholder="39.289012" />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground -mt-1">
                Use decimal-degree GPS values. Latitude: <span className="font-mono">-90 to 90</span>. Longitude: <span className="font-mono">-180 to 180</span>. Tanzania ranges roughly lat <span className="font-mono">-12 to -1</span>, lng <span className="font-mono">29 to 41</span>.
              </p>
              {(() => {
                const msg = detectCoordIssue(form.latitude, form.longitude);
                return msg ? (
                  <Alert variant="destructive" className="py-2">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-xs">{msg}</AlertDescription>
                  </Alert>
                ) : null;
              })()}
              <Button type="button" variant="outline" size="sm" onClick={useCurrentLocation}>
                <Crosshair className="w-3 h-3 mr-1" /> Use my current location
              </Button>
              <div>
                <Label className="text-xs">Geofence radius (meters)</Label>
                <Input type="number" value={form.radius_m} onChange={(e) => setForm({ ...form, radius_m: e.target.value })} />
                <p className="text-[11px] text-muted-foreground mt-1">Group leaders can only scan when within this radius of the site.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={loading}>{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Coordinates</TableHead>
              <TableHead>Radius</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sites.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No field sites yet.</TableCell></TableRow>
            ) : sites.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{s.address ?? "—"}</TableCell>
                <TableCell className="font-mono text-xs">{Number(s.latitude).toFixed(5)}, {Number(s.longitude).toFixed(5)}</TableCell>
                <TableCell>{s.radius_m} m</TableCell>
                <TableCell>
                  <Badge variant={s.active ? "default" : "secondary"} className="cursor-pointer" onClick={() => toggleActive(s)}>
                    {s.active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(s)}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
